import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon, TimerIcon } from 'lucide-react';
const Chaga = () => {
  const [activeSection, setActiveSection] = useState('endurance');
  return <div className="w-full bg-white" data-id="element-56">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-blue-50 to-blue-100" data-id="element-57">
        <div className="max-w-4xl mx-auto" data-id="element-58">
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-8 rounded-full" data-id="element-59"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-60">
            Endurance — Chaga
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-61">
            Sustained vitality with sweet warmth.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-62">
        <div className="container mx-auto" data-id="element-63">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-64">
            Chaga Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-65">
            Our proprietary extraction technology unlocks the full spectrum of
            Chaga bioactives, delivering nine distinct benefit categories in a
            single cup.
          </p>
          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-66">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-67">
              Chaga Spora | Endurance Vitality + Immunonutrients + Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-68">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-69">
              <nav className="flex flex-col space-y-4" data-id="element-70">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'endurance' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('endurance')} data-id="element-71">
                  <div className="flex items-center gap-3" data-id="element-72">
                    <TimerIcon className="w-5 h-5" data-id="element-73" />
                    <span data-id="element-74">Endurance Vitality</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-75">
                  <div className="flex items-center gap-3" data-id="element-76">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-77" />
                    <span data-id="element-78">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-79">
                  <div className="flex items-center gap-3" data-id="element-80">
                    <ShieldIcon className="w-5 h-5" data-id="element-81" />
                    <span data-id="element-82">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-83">
              {/* Endurance Vitality Section */}
              <section id="endurance" className={`pb-24 ${activeSection === 'endurance' ? 'block' : 'hidden'}`} data-id="element-84">
                <h2 className="text-3xl font-bold mb-8" data-id="element-85">
                  The Endurance Vitality of Chaga
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-86">
                  Chaga supports mitochondrial energy output, vascular
                  integrity, and respiratory function for consistent vitality
                  throughout the day.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-87">
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-88">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-89">
                      <ZapIcon className="w-6 h-6" data-id="element-90" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-91">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-92">
                      Consistent vitality
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-93">
                      <span className="font-medium" data-id="element-94">Experience:</span>{' '}
                      Consistent vitality across the day.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-95">
                      <span className="font-medium" data-id="element-96">Why it works:</span>{' '}
                      Polysaccharides support mitochondrial energy output.
                    </p>
                    <div className="mt-auto" data-id="element-97">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-98">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-99">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-100">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-101">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-102">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-103" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-104">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-105">
                      Stable circulation
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-106">
                      <span className="font-medium" data-id="element-107">Experience:</span> Stable
                      circulation, even under exertion.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-108">
                      <span className="font-medium" data-id="element-109">Why it works:</span> Melanin
                      complexes support vascular integrity.
                    </p>
                    <div className="mt-auto" data-id="element-110">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-111">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-112">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-113">
                          Melanin complexes
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-114">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-115">
                      <div className="w-6 h-6" data-id="element-116" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-117">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-118">
                      Efficient breathing
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-119">
                      <span className="font-medium" data-id="element-120">Experience:</span> Deeper,
                      more efficient breathing.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-121">
                      <span className="font-medium" data-id="element-122">Why it works:</span>{' '}
                      Polysaccharides support lung immune defense.
                    </p>
                    <div className="mt-auto" data-id="element-123">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-124">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-125">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-126">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-127">
                <h2 className="text-3xl font-bold mb-8" data-id="element-128">
                  The Resilience Matrix
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-129">
                  Chaga contains unique compounds that strengthen immune system
                  defense, support systemic balance during stress, and improve
                  metabolic efficiency.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-130">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-131">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-132">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-133" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-134">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-135">
                      Stress resilience
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-136">
                      <span className="font-medium" data-id="element-137">Experience:</span> Keeps
                      illness at bay during stress.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-138">
                      <span className="font-medium" data-id="element-139">Why it works:</span>{' '}
                      β-glucans and polysaccharides strengthen immune system
                      defense.
                    </p>
                    <div className="mt-auto" data-id="element-140">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-141">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-142">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-143">
                          β-glucans
                        </span>
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-144">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-145">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-146">
                      <CircleIcon className="w-6 h-6" data-id="element-147" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-148">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-149">
                      Steady endurance
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-150">
                      <span className="font-medium" data-id="element-151">Experience:</span> Steady
                      endurance under pressure.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-152">
                      <span className="font-medium" data-id="element-153">Why it works:</span>{' '}
                      Betulinic acid supports systemic balance during stress.
                    </p>
                    <div className="mt-auto" data-id="element-154">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-155">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-156">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-157">
                          Betulinic acid
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Endocrine Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-158">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-159">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-160" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-161">
                      Endocrine Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-162">
                      Metabolic efficiency
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-163">
                      <span className="font-medium" data-id="element-164">Experience:</span> Improved
                      metabolic efficiency.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-165">
                      <span className="font-medium" data-id="element-166">Why it works:</span> Betulin
                      derivatives balance insulin sensitivity.
                    </p>
                    <div className="mt-auto" data-id="element-167">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-168">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-169">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-170">
                          Betulin derivatives
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-171">
                <h2 className="text-3xl font-bold mb-8" data-id="element-172">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-173">
                  Chaga contains powerful compounds that neutralize oxidative
                  stress, reduce systemic inflammation, and protect neural
                  tissues from damage.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-174">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-175">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-176">
                      <ShieldIcon className="w-6 h-6" data-id="element-177" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-178">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-179">
                      Powerful defense
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-180">
                      <span className="font-medium" data-id="element-181">Experience:</span> Powerful
                      defense against aging and fatigue.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-182">
                      <span className="font-medium" data-id="element-183">Why it works:</span> Melanin
                      and polyphenols neutralize oxidative stress at high
                      capacity.
                    </p>
                    <div className="mt-auto" data-id="element-184">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-185">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-186">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-187">
                          Melanin
                        </span>
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-188">
                          Polyphenols
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-189">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-190">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-191" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-192">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-193">
                      Faster recovery
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-194">
                      <span className="font-medium" data-id="element-195">Experience:</span> Faster
                      recovery and reduced chronic fatigue.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-196">
                      <span className="font-medium" data-id="element-197">Why it works:</span>{' '}
                      Betulinic acid and sterols reduce systemic inflammation.
                    </p>
                    <div className="mt-auto" data-id="element-198">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-199">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-200">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-201">
                          Betulinic acid
                        </span>
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-202">
                          Sterols
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Neuroprotective Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-203">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-204">
                      <BrainIcon className="w-6 h-6" data-id="element-205" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-206">Neuroprotective</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-207">
                      Mental endurance
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-208">
                      <span className="font-medium" data-id="element-209">Experience:</span> Sharper
                      mind for the long haul.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-210">
                      <span className="font-medium" data-id="element-211">Why it works:</span>{' '}
                      Antioxidant melanin protects neural tissues from oxidative
                      damage.
                    </p>
                    <div className="mt-auto" data-id="element-212">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-213">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-214">
                        <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-215">
                          Antioxidant melanin
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-216">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-217">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-218">
                Chaga Spora | Endurance Vitality + Immunonutrients +
                Antioxidants
              </p>
            </div>
            <div data-id="element-219">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'endurance' ? '' : 'endurance')} data-id="element-220">
                <div className="flex items-center gap-3" data-id="element-221">
                  <TimerIcon className="w-5 h-5" data-id="element-222" />
                  <h2 className="text-xl font-bold" data-id="element-223">Endurance Vitality</h2>
                </div>
                <span data-id="element-224">{activeSection === 'endurance' ? '−' : '+'}</span>
              </button>
              {activeSection === 'endurance' && <div className="p-4 space-y-8" data-id="element-225">
                  {/* Mobile content for Endurance Vitality section */}
                </div>}
            </div>
            <div data-id="element-226">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-227">
                <div className="flex items-center gap-3" data-id="element-228">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-229" />
                  <h2 className="text-xl font-bold" data-id="element-230">The Resilience Matrix</h2>
                </div>
                <span data-id="element-231">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-232">
                  {/* Mobile content for Immunonutrients section */}
                </div>}
            </div>
            <div data-id="element-233">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-234">
                <div className="flex items-center gap-3" data-id="element-235">
                  <ShieldIcon className="w-5 h-5" data-id="element-236" />
                  <h2 className="text-xl font-bold" data-id="element-237">The Protection Network</h2>
                </div>
                <span data-id="element-238">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-239">
                  {/* Mobile content for Antioxidants section */}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-240">
        <div className="max-w-4xl mx-auto" data-id="element-241">
          <h2 className="text-3xl font-bold mb-6" data-id="element-242">Experience Chaga Spora</h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-243">
            Sustained vitality with sweet warmth that keeps you going through
            life's challenges.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-244">
            <Link to="/#products" className="bg-blue-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-blue-600 transition-colors" data-id="element-245">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-246">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-247">
              <span data-id="element-248">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-249" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default Chaga;